import { apiService } from '../../api/apiService';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ApiAssetType,
  ApiCertificateInstance,
  ApiCertificateTemplate,
  ApiDocumentDoc
} from '@agunity/api-v4/build/data-contracts';
import { CustomFieldItemType } from '../../Constants';
import { ApiItemTypes, GenericHttpResult } from '@agunity/api-v4';

export interface UploadFileProps {
  documentDetails?: string;
  ContentType?: string;
  ContentDisposition?: string;
  Headers?: Record<string, string[]>;
  Length?: number;
  Name?: string;
  FileName?: string;
}

export interface CertificateState {
  addNewCertificateType: string;
  certificateBreadcrumbs: string;
  hasCertificateChanged: boolean;
  isOpenCertificateUnsavedModal: boolean;
  certificateTypes: ApiCertificateTemplate[];
  reFetchData: boolean;
  currencyOptions?: ApiAssetType[]
}

const initialState: CertificateState = {
  addNewCertificateType: '',
  certificateBreadcrumbs: '',
  hasCertificateChanged: false,
  isOpenCertificateUnsavedModal: false,
  certificateTypes: [],
  reFetchData: false,
  currencyOptions: []
};

export const CertificateSlice = createSlice({
  name: 'certificate',
  initialState,
  reducers: {
    setAddNewCertificateType: (
      state: CertificateState,
      action: PayloadAction<string>
    ) => {
      state.addNewCertificateType = action.payload;
    },
    setCertificateBreadcrumbs: (
      state: CertificateState,
      action: PayloadAction<string>
    ) => {
      state.certificateBreadcrumbs = action.payload;
    },
    setHasCertificateChanged: (
      state: CertificateState,
      action: PayloadAction<boolean>
    ) => {
      state.hasCertificateChanged = action.payload;
    },
    setIsOpenCertificateUnsavedModal: (
      state: CertificateState,
      action: PayloadAction<boolean>
    ) => {
      state.isOpenCertificateUnsavedModal = action.payload;
    },
    setCertificateTypes: (
      state: CertificateState,
      action: PayloadAction<ApiCertificateTemplate[]>
    ) => {
      state.certificateTypes = action.payload;
    },
    setReFetchData: (
      state: CertificateState,
      action: PayloadAction<boolean>
    ) => {
      state.reFetchData = action.payload;
    },
    setCurrencyOptions: (
      state: CertificateState,
      action: PayloadAction<ApiAssetType[]>
    ) => {
      state.currencyOptions = action.payload;
    },
    clearCertificateCache: (
      state: CertificateState
    ) => {
      Object.assign(state, initialState);
    }
  }
});

export const {
  setAddNewCertificateType,
  setCertificateBreadcrumbs,
  setHasCertificateChanged,
  setIsOpenCertificateUnsavedModal,
  setCertificateTypes,
  setReFetchData,
  setCurrencyOptions,
  clearCertificateCache
} = CertificateSlice.actions;

export default CertificateSlice.reducer;

export const getCertificateList =
  () =>
    async () => {
      return await apiService.certificateV10ListList();
    };

export const getCertificateType =
  () =>
    async () => {
      return await apiService.certificateV10TypesList();
    };

export const getCertificateById =
  (id: number) =>
    async () => {
      return await apiService.certificateV10ItemDetail(id);
    };

export const createCertificate =
  (data: ApiCertificateInstance) =>
    async () => {
      return await apiService.certificateV10ItemCreate(data);
    };

export const updateCertificate =
  (data: ApiCertificateInstance) =>
    async () => {
      return await apiService.certificateV10ItemPartialUpdate(data);
    };

export const deleteCertificate =
  (id: number) =>
    async () => {
      return await apiService.certificateV10ItemDelete(id);
    };

export const getCertificateDocuments =
  (id: number) =>
    async () => {
      // 55 is for certificate
      return await apiService.documentV10ListDetail(CustomFieldItemType.Certificate, id);
    };

export const uploadCertificateDocuments =
  (data: ApiDocumentDoc) =>
    async () => {
      return await apiService.documentV12UploadViaBodyCreate(data);
    };

export const getCertificateOrgNameById =
  (id: number) =>
    async () => {
      return await apiService.organizationV10NameDetail(id);
    };

export const getCertificateDocumentById =
  async (documentKey: string) =>
    async () => {
      // may need to comment out headers['contenttype'] === 'application/octet-stream' in the future, cannot download pdf possibly due to responseType: 'json'
      return await apiService.documentV10DownloadDetail(documentKey);
    };

export const deleteDocumentByKey =
  (key: string) =>
    async () => {
      return await apiService.documentV10DeleteDelete(key);
    };

export const getCurrencies =
  () =>
    async () => {
      return await apiService.licenseV10CurrenciesList();
    };

export const getCoopList =
  () =>
    async () => {
      return await apiService.memberV10CoopListList();
    };

export const getImageBlob = (typeId: ApiItemTypes, id: number) => {
  return async (): Promise<GenericHttpResult<string>> => {
    const response = await apiService.blobV30Base64Detail(typeId, id);
    return response;
  };
};